import React from 'react';

import { Box, Flex, Space, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { PuzzleIcon } from 'hugeicons-react';
import { OverviewCard } from 'pages/GuestSmartAssistant/GuestDashboard/OverviewTab/OverviewCard';
import { useTranslation } from 'react-i18next';
import { GuestSmartAssistantService } from 'services/guest-smart-assistant-service';
import { GUEST_SMART_ASSISTANT_WITH_INTEGRATIONS_QUERY_KEY } from 'shared/constants/query-keys';
import { SmartAssistant, SmartAssistantType, SmartAssistantWithIntegrations } from 'shared/interfaces/SmartAssistant';
import { PhoneNumberProvider } from 'shared/interfaces/Telephony';
import { SMART_ASSISTANT_TYPE_DATA } from 'shared/utils/smart-assistant';

interface IntegrationsOverviewProps {
  smartAssistant: SmartAssistant;
}

export function IntegrationsOverview({ smartAssistant }: IntegrationsOverviewProps) {
  const { t } = useTranslation();

  const guestSmartAssistantDataQuery = useQuery({
    queryKey: [GUEST_SMART_ASSISTANT_WITH_INTEGRATIONS_QUERY_KEY, { smartAssistantId: smartAssistant._id }],
    queryFn: () => GuestSmartAssistantService.getGuestSmartAssistantDataWithIntegrations(smartAssistant._id),
    retry: false,
    refetchOnWindowFocus: false
  });

  const getIntegrationItem = (smartAssistantWithIntegrations?: SmartAssistantWithIntegrations) => {
    if (!smartAssistantWithIntegrations) return undefined;
    const typeItem = SMART_ASSISTANT_TYPE_DATA[smartAssistantWithIntegrations.type];

    const getLabel = () => {
      if (smartAssistantWithIntegrations.type === SmartAssistantType.SHOPIFY) {
        return smartAssistantWithIntegrations.shopifyStoreResource?.shopifyResourceConfig?.store.shop;
      }
      if (smartAssistantWithIntegrations.type === SmartAssistantType.WHATSAPP) {
        const whatsAppPhoneNumber = smartAssistantWithIntegrations.whatsAppIntegration?.whatsAppPhoneNumberId;
        return smartAssistantWithIntegrations.whatsAppIntegration?.whatsAppAccount.phoneNumbers.find(
          (phoneNumber) => phoneNumber.whatsAppId === whatsAppPhoneNumber
        )?.displayedNumber;
      }

      if (smartAssistantWithIntegrations.type === SmartAssistantType.FACEBOOK) {
        return smartAssistantWithIntegrations.facebookIntegration?.facebookAccount.name;
      }

      if (smartAssistantWithIntegrations.type === SmartAssistantType.INSTAGRAM) {
        return `@${smartAssistantWithIntegrations.instagramIntegration?.instagramAccount.username}`;
      }

      if (smartAssistantWithIntegrations.type === SmartAssistantType.DISCORD) {
        return smartAssistantWithIntegrations.discordIntegration?.serverId;
      }

      if (smartAssistantWithIntegrations.type === SmartAssistantType.PHONE) {
        const provider = smartAssistantWithIntegrations.voiceIntegration?.telephonyConfiguration?.phoneNumber.provider;
        if (provider === PhoneNumberProvider.TWILIO) {
          return smartAssistantWithIntegrations.voiceIntegration?.telephonyConfiguration?.phoneNumber
            .twilioPhoneNumberConfiguration.friendlyPhoneNumber;
        }
      }

      return undefined;
    };

    return {
      type: typeItem.title,
      icon: typeItem.icon(20),
      label: getLabel()
    };
  };

  const integrationItem = getIntegrationItem(guestSmartAssistantDataQuery.data?.smartAssistant);

  return (
    <OverviewCard title={t('SMART_ASSISTANT.OVERVIEW_SECTION.INTEGRATIONS.TITLE')} icon={<PuzzleIcon />}>
      {integrationItem && (
        <Flex direction="row" align="center" w="100%" justify="space-between">
          <Box>
            <Text size="sm" c="dimmed">
              {integrationItem.type}
            </Text>
            <Space h={3} />
            <Text size="md">{integrationItem.label}</Text>
          </Box>
          <Box
            sx={(theme) => ({
              backgroundColor: theme.colors.gray[0],
              borderRadius: theme.radius.xl,
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            })}>
            {integrationItem.icon}
          </Box>
        </Flex>
      )}
    </OverviewCard>
  );
}
